
import React, { useRef, useEffect } from 'react';
import LiveSessionParticipant from './LiveSessionParticipant';
import { useVideoStreamSetup } from '@/hooks/useVideoStreamSetup';
import { useMicrophoneStream } from '@/hooks/useMicrophoneStream';
import { useWebRTC } from '@/hooks/useWebRTC';
import { useToast } from '@/hooks/use-toast';

interface Participant {
  name: string;
  role: string;
  avatarUrl: string;
  isLocal?: boolean;
  isMuted?: boolean;
  isVideoOff?: boolean;
  isScreenSharing?: boolean;
}

interface LiveSessionParticipantsViewProps {
  participants: Participant[];
  isMicActive: boolean;
  isVideoActive: boolean;
  isScreenSharing?: boolean;
  screenStream?: MediaStream | null;
  sessionId: string;
  userId: string;
  displayName: string;
  onMicLevelChange?: (level: number) => void;
}

const LiveSessionParticipantsView: React.FC<LiveSessionParticipantsViewProps> = ({
  participants,
  isMicActive,
  isVideoActive,
  isScreenSharing = false,
  screenStream = null,
  sessionId,
  userId,
  displayName,
  onMicLevelChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [enlargedParticipantIndex, setEnlargedParticipantIndex] = React.useState<number | null>(null);
  const { toast } = useToast();
  
  // Separate audio and video streams
  const { stream: videoStream, cameraStreamReady, videoRef } = useVideoStreamSetup({
    isVideoActive
  });
  
  const { micStream, micReady, audioLevel } = useMicrophoneStream({
    isMicActive
  });
  
  // WebRTC for real-time communication
  const { remoteParticipants, addTrack, removeTrack } = useWebRTC({
    sessionId,
    userId,
    displayName,
    enabled: true
  });
  
  // Add/remove video track when camera changes
  useEffect(() => {
    if (videoStream && cameraStreamReady) {
      const videoTrack = videoStream.getVideoTracks()[0];
      if (videoTrack) {
        console.log('[ParticipantsView] Adding video track to WebRTC');
        addTrack(videoTrack);
        return () => {
          console.log('[ParticipantsView] Removing video track from WebRTC');
          removeTrack(videoTrack);
        };
      }
    }
  }, [videoStream, cameraStreamReady, addTrack, removeTrack]);
  
  // Add/remove audio track when mic changes
  useEffect(() => {
    if (micStream && micReady) {
      const audioTrack = micStream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[ParticipantsView] Adding audio track to WebRTC');
        addTrack(audioTrack);
        return () => {
          console.log('[ParticipantsView] Removing audio track from WebRTC');
          removeTrack(audioTrack);
        };
      }
    }
  }, [micStream, micReady, addTrack, removeTrack]);
  
  // Add/remove screen share track
  useEffect(() => {
    if (screenStream && isScreenSharing) {
      const screenTrack = screenStream.getVideoTracks()[0];
      if (screenTrack) {
        console.log('[ParticipantsView] Adding screen share track to WebRTC');
        addTrack(screenTrack);
        return () => {
          console.log('[ParticipantsView] Removing screen share track from WebRTC');
          removeTrack(screenTrack);
        };
      }
    }
  }, [screenStream, isScreenSharing, addTrack, removeTrack]);
  
  // Propagate microphone audio level changes
  useEffect(() => {
    onMicLevelChange?.(audioLevel);
  }, [audioLevel, onMicLevelChange]);
  
  const screenSharingIndex = participants.findIndex(p => p.isScreenSharing);
  
  const isAnyParticipantEnlarged = enlargedParticipantIndex !== null || screenSharingIndex !== -1;
  
  const effectiveEnlargedIndex = enlargedParticipantIndex !== null ? enlargedParticipantIndex : screenSharingIndex;
  
  // Combine local participants with remote WebRTC participants
  const allParticipants = [
    ...participants,
    ...remoteParticipants.map(rp => ({
      name: rp.displayName,
      role: 'Remote Participant',
      avatarUrl: '',
      isLocal: false,
      isMuted: false,
      isVideoOff: false,
      isScreenSharing: false,
      remoteStream: rp.stream
    }))
  ];

  const getGridColsClass = () => {
    const count = allParticipants.length;
    
    if (isAnyParticipantEnlarged) {
      return "grid-cols-1 md:grid-cols-4";
    }
    
    if (count <= 1) {
      return "grid-cols-1";
    } else if (count <= 2) {
      return "grid-cols-1 md:grid-cols-2";
    } else if (count <= 4) {
      return "grid-cols-2";
    } else {
      return "grid-cols-2 md:grid-cols-3";
    }
  };

  const toggleEnlargeParticipant = (index: number) => {
    if (enlargedParticipantIndex === index) {
      setEnlargedParticipantIndex(null);
    } else {
      setEnlargedParticipantIndex(index);
    }
  };

  return (
    <div className="h-full w-full flex flex-col p-2 overflow-auto">
      <div ref={containerRef} className={`grid ${getGridColsClass()} gap-3 w-full auto-rows-fr`}>
        {allParticipants.map((participant, index) => {
          const isEnlarged = effectiveEnlargedIndex === index;
          
          let cellClass = "";
          if (isEnlarged) {
            cellClass = "col-span-3 row-span-2";
          } else if (isAnyParticipantEnlarged) {
            cellClass = "col-span-1";
          }

          return (
            <LiveSessionParticipant 
              key={index}
              name={participant.name}
              role={participant.role}
              avatarUrl={participant.avatarUrl}
              isLocal={participant.isLocal}
              isMuted={participant.isLocal ? !isMicActive : participant.isMuted}
              isVideoOff={participant.isLocal ? !isVideoActive : participant.isVideoOff}
              isScreenSharing={participant.isScreenSharing}
              isEnlarged={isEnlarged}
              onToggleEnlarge={() => toggleEnlargeParticipant(index)}
              className={cellClass}
              screenStream={participant.isLocal && isScreenSharing ? screenStream : null}
              localVideoRef={participant.isLocal ? videoRef : undefined}
              localStream={participant.isLocal && cameraStreamReady ? videoStream : null}
              remoteStream={(participant as any).remoteStream}
              audioLevel={participant.isLocal ? audioLevel : 0}
            />
          );
        })}
      </div>
    </div>
  );
};

export default LiveSessionParticipantsView;
